import { useSelector } from 'react-redux'

import {
  selectAllowWrittenFeedbackCollection,
  selectPersistedAllowWrittenFeedbackCollection,
} from 'slices/orderFeedbackCollection/selectors'
import { useFeedbackCollectionFormInitialize } from './useFeedbackCollectionFormInitialize'

// Answers one question for the Settings tab: is the merchant collecting written
// feedback right now? SAVED value, not the edited one. The "Your current setup"
// card describes what guests are getting today, so it must not react to an edit
// nobody has published. It appears only once Publish turns collection on, and
// stays until Publish turns it off.
//
// It reads the `orderFeedbackCollection` slice — the same slice the Feedback
// collection sub-tab writes — so there is exactly one source of truth.
//
// It also HYDRATES that slice, and has to. The sub-nav mounts one pane at a
// time (`unmountInactive`), so a caller outside the Feedback collection pane
// cannot assume that pane's own initialize hook has run. Hydration is NOT a
// second fetch: `useFeedbackCollectionFormInitialize` projects the
// already-loaded current merchant out of redux-orm, and it is idempotent, so
// calling it here and in the pane costs one identical `resetForm` dispatch each.
//
// This is ALSO what every FR-58 dependent-control gate reads (Reply address,
// Guest experience, Notifications). Prototype v70 was measured live on that
// point: with "Collect written feedback" toggled off but not yet published,
// the dependent panes are untouched — full opacity, every control live, no
// gate note. They gate only once Publish lands, which is the same "the toggle
// only STAGES a change" contract the Feedback collection card itself follows.
export const useWrittenFeedbackPersisted = (): boolean => {
  useFeedbackCollectionFormInitialize()

  return useSelector(selectPersistedAllowWrittenFeedbackCollection)
}

// The LIVE counterpart: the EDITED value, not the saved one — the slice's
// staged toggle, before Publish.
//
// NOT the FR-58 gate signal. An earlier build gated the dependent panes on
// this hook so a merchant saw them react to the toggle immediately; v70 was
// then measured live and gates only after Publish, so every pane moved to
// `useWrittenFeedbackPersisted` above. Kept as the slice's edited-value
// accessor, the counterpart to the persisted one, and to keep the staged and
// saved readings named apart rather than re-derived ad hoc at a call site.
//
// Same hydration story as the persisted hook: the sub-nav mounts one pane at a
// time, so a caller outside the Feedback collection pane cannot assume that
// pane's own initialize hook has run. The double `resetForm` dispatch this
// causes when both hooks mount is cheap and idempotent, not duplication to
// clean up (see the persisted hook's own comment above).
export const useWrittenFeedbackEnabled = (): boolean => {
  useFeedbackCollectionFormInitialize()

  return useSelector(selectAllowWrittenFeedbackCollection)
}
